import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {createCatalogService} from '../../../services/catalogAdminService'
import {handleApiError} from '../../../utils/handleApiError'
import {CatalogConfig, CatalogItem} from '../types'
import {filterCatalog, StatusFilter} from '../utils/catalogFilter'
import {buildServerParams, mapServerResult, Sort} from '../utils/serverTable'

const DEFAULT_FETCH_LIMIT = 1000
// Page size used when paging the whole dataset out of the server for a CSV export.
const EXPORT_PAGE_SIZE = 200

// The current view request. `sort` is only meaningful in server-side mode (the client-side path
// keeps its original API-stable behaviour and ignores it).
export interface CatalogQuery {
  search: string
  status: StatusFilter
  sort: Sort | null
  page: number
  pageSize: number
}

// Drives the catalog list in EITHER mode from one config:
//  - Client-side (default): fetch the full (small) catalog once, filter/paginate in memory via
//    filterCatalog — unchanged pilot behaviour; `truncated` warns when the catalog outgrows it.
//  - Server-side (config.serverSide): fetch one page at a time, pushing search/status/sort down as
//    query params and trusting the DRF `count`. This is what the loyalty member-balances table and
//    the B1.5 Cities mount need. Returns the rendered page plus loading/error, a `reload` for use
//    after mutations, and `fetchAllForExport` for CSV.
export const useCatalogAdmin = (config: CatalogConfig, query: CatalogQuery) => {
  const service = useMemo(() => createCatalogService(config.endpoint), [config.endpoint])
  const serverSide = Boolean(config.serverSide)
  const {search, status, sort, page, pageSize} = query

  // Client-side state: the full catalog + the "more rows exist than we fetched" warning flag.
  const [all, setAll] = useState<CatalogItem[]>([])
  const [truncated, setTruncated] = useState(false)
  // Server-side state: just the current page of rows + the server's total count.
  const [serverPage, setServerPage] = useState<{items: CatalogItem[]; total: number}>({
    items: [],
    total: 0,
  })

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const loadAll = useCallback(async () => {
    // Guard the fallback config used while an unknown slug redirects to 404.
    if (!config.endpoint) return
    try {
      setLoading(true)
      setError(false)
      const data = await service.list({limit: config.fetchLimit ?? DEFAULT_FETCH_LIMIT, offset: 0})
      const results = data?.results ?? []
      if (isMountedRef.current) {
        setAll(results)
        setTruncated(typeof data?.count === 'number' && data.count > results.length)
      }
    } catch (err) {
      if (isMountedRef.current) setError(true)
      handleApiError(err, {})
    } finally {
      if (isMountedRef.current) setLoading(false)
    }
  }, [service, config.endpoint, config.fetchLimit])

  // Refetch whenever a server-driving input changes. Serialised into a string so the callback
  // depends on the query VALUES, not the (fresh-each-render) query object identity.
  const serverKey = serverSide
    ? JSON.stringify({search: search.trim(), status, sort, page, pageSize})
    : ''

  const loadPage = useCallback(async () => {
    if (!config.endpoint) return
    try {
      setLoading(true)
      setError(false)
      const data = await service.list(
        buildServerParams(config, {search, status, sort, page, pageSize})
      )
      if (isMountedRef.current) {
        const {items, total} = mapServerResult(data, pageSize)
        setServerPage({items, total})
      }
    } catch (err) {
      if (isMountedRef.current) setError(true)
      handleApiError(err, {})
    } finally {
      if (isMountedRef.current) setLoading(false)
    }
    // config is stable per screen mount; serverKey captures the changing query inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, config.endpoint, serverKey])

  useEffect(() => {
    if (serverSide) loadPage()
    else loadAll()
  }, [serverSide, loadPage, loadAll])

  // Refetch the CURRENT view after a create/update/toggle mutation.
  const reload = serverSide ? loadPage : loadAll

  const view = useMemo(() => {
    if (serverSide) {
      const total = serverPage.total
      const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)))
      return {items: serverPage.items, total, totalPages}
    }
    return filterCatalog(all, config, {search, status, page, pageSize})
  }, [serverSide, serverPage, all, config, search, status, page, pageSize])

  // Fetch EVERY matching row (across pages) for a CSV export, honouring the active search/status/
  // sort. Client-side mode already holds the full set, so it just returns `all`.
  const fetchAllForExport = useCallback(async (): Promise<CatalogItem[]> => {
    if (!serverSide) return all
    const rows: CatalogItem[] = []
    let offset = 0
    // Bounded loop: stop on an empty batch or once we've collected the reported total; the guard
    // is a backstop against a count/results mismatch looping forever.
    for (let guard = 0; guard < 1000; guard++) {
      const params = buildServerParams(config, {
        search,
        status,
        sort,
        page: 1,
        pageSize: EXPORT_PAGE_SIZE,
      })
      params.limit = EXPORT_PAGE_SIZE
      params.offset = offset
      const data = await service.list(params)
      const batch = data?.results ?? []
      rows.push(...batch)
      const total = typeof data?.count === 'number' ? data.count : rows.length
      offset += EXPORT_PAGE_SIZE
      if (batch.length === 0 || rows.length >= total) break
    }
    return rows
  }, [serverSide, all, service, config, search, status, sort])

  return {
    ...view,
    loading,
    error,
    // Only the client-side fetch-all path can be truncated; server-side pages the whole set.
    truncated: serverSide ? false : truncated,
    reload,
    service,
    fetchAllForExport,
  }
}
